import axios from "axios";
import DatePicker from "react-datepicker";
import { Toast, ToastContainer } from "react-bootstrap";

import { getToken, formatDateTime } from "../../../utils/utils";
import { useState, useRef, useEffect, useCallback } from "react";
import { PAYMENT_METHOD } from "../../../constants/sharedConstans";
import { ENDPOINT } from "../../../constants/endpointConstants";

const NewExpense = ({
  pushNewExpense,
  expensesCategories,
  invoiceId,
  isUpdateMode = false,
  onUpdateSuccess,
  onClose,
}) => {
  console.log("expensesCategories", expensesCategories);
  const [date, setDate] = useState(new Date());
  const [description, setDescription] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [imageSrc, setImageSrc] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [errorMsg] = useState("");
  const [toasts, setToasts] = useState([]);
  const [expenseSplits, setExpenseSplits] = useState([
    { categoryId: "", price: "", amount: "" },
  ]);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [nextFocus, setNextFocus] = useState(null);

  const fileInputRef = useRef(null);
  const totalPriceRef = useRef(null);
  const addSplitRef = useRef(null);
  const paymentMethodRef = useRef(null);
  const descriptionRef = useRef(null);
  const submitRef = useRef(null);
  const splitRefs = useRef({ category: [], price: [], amount: [] });
  const mouseInteractedRef = useRef(false);

  const fetchInvoiceData = useCallback(async () => {
    const token = getToken();
    setIsLoadingInvoice(true);
    try {
      const { data } = await axios.get(
        `${ENDPOINT.GET_INVOICE}/${invoiceId}`,
        token
      );
      const parsedDate = data.invoiceDate
        ? new Date(data.invoiceDate)
        : new Date();
      setDate(isNaN(parsedDate.getTime()) ? new Date() : parsedDate);
      setDescription(data.description || "");
      setTotalPrice(data.totalPrice?.toString() || "");
      setPaymentMethodId(data.paymentMethodId?.toString() || "");
      setImageSrc(
        `https://que-bendicion-invoices.s3.us-west-2.amazonaws.com/${data.invoiceImagePath}`
      );
      setImageChanged(false);
      setCacheBuster(Date.now());

      if (data.expenses && data.expenses.length > 0) {
        setExpenseSplits(
          data.expenses.map((expense) => ({
            categoryId: expense.categoryId?.toString() || "",
            price: expense.price?.toString() || "",
            amount: expense.amount?.toString() || "1",
          }))
        );
      }

      setIsLoadingInvoice(false);
    } catch {
      showToast("Error al cargar los datos de la factura", "danger");
      setIsLoadingInvoice(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (isUpdateMode && invoiceId) {
      fetchInvoiceData();
    }
  }, [isUpdateMode, invoiceId, fetchInvoiceData]);

  const focusDateInput = () => {
    const element = document.getElementById(
      isUpdateMode ? "update-expense-date-picker" : "new-expense-date-picker"
    );
    if (element) {
      element.focus();
      const length = element.value ? element.value.length : 0;
      if (length >= 2) {
        element.setSelectionRange(length - 2, length);
      }
    }
  };

  useEffect(() => {
    if (isUpdateMode) return;
    const timer = setTimeout(focusDateInput, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!nextFocus) return;
    const { field, index } = nextFocus;
    const element =
      field === "category"
        ? splitRefs.current.category[index]
        : field === "price"
          ? splitRefs.current.price[index]
          : splitRefs.current.amount[index];
    if (element) {
      element.focus();
    }
    setNextFocus(null);
  }, [nextFocus, expenseSplits]);

  const totalPriceChange = (event) => {
    const value = event.target.value;
    if (value === "") {
      setTotalPrice(0);
    } else {
      setTotalPrice(parseInt(value));
    }
  };

  const descriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const datePickerOnChange = (update) => {
    setDate(update);
  };

  const buttonContent = () => {
    if (loading === false) {
      return isUpdateMode ? "Actualizar" : "Agregar";
    } else {
      return (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
      );
    }
  };

  const addNewExpense = async () => {
    const validSplits = expenseSplits.filter(
      (split) => split.categoryId && split.price && split.amount
    );
    const expenseData = {
      description,
      date: formatDateTime(date),
      paymentType: parseInt(paymentMethodId),
      price: totalPrice,
      categories: validSplits.map((split) => ({
        categoryId: parseInt(split.categoryId),
        price: parseInt(split.price),
        amount: parseInt(split.amount),
      })),
    };

    if (!isUpdateMode || imageChanged) {
      expenseData.invoice = imageSrc;
    }

    if (isUpdateMode) {
      expenseData.id = invoiceId;
    }

    const token = getToken();
    setLoading(true);
    try {
      let response;
      if (isUpdateMode) {
        response = await axios.put(ENDPOINT.UPDATE_INVOICE, expenseData, token);
        showToast("Factura actualizada exitosamente", "success");
        setCacheBuster(Date.now());
        if (onUpdateSuccess) onUpdateSuccess();
        if (onClose) onClose();
      } else {
        response = await axios.post(ENDPOINT.NEW_INVOICE, expenseData, token);
        showToast("Gasto agregado exitosamente", "success");
        pushNewExpense(response);
        resetNewExpense();
        setTimeout(focusDateInput, 0);
      }
      setLoading(false);
    } catch {
      setLoading(false);
      showToast(
        `Error al ${isUpdateMode ? "actualizar" : "agregar"} el gasto`,
        "danger"
      );
    }
  };

  const showToast = (message, variant = "success") => {
    const id = new Date().getTime();
    setToasts((prevToasts) => [...prevToasts, { id, message, variant }]);
  };

  const resetNewExpense = () => {
    if (!isUpdateMode) {
      setDate(new Date());
      setDescription("");
      setTotalPrice("");
      setPaymentMethodId("");
      setImageSrc(null);
      setImageChanged(false);
      setExpenseSplits([{ categoryId: "", price: "", amount: "" }]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageDataUrl = reader.result;
        setImageSrc(imageDataUrl);
        setImageChanged(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const addSplit = () => {
    setExpenseSplits([
      ...expenseSplits,
      { categoryId: "", price: "", amount: "" },
    ]);
  };

  const removeSplit = (index) => {
    const newSplits = expenseSplits.filter((_, i) => i !== index);
    setExpenseSplits(
      newSplits.length > 0
        ? newSplits
        : [{ categoryId: "", price: "", amount: "" }]
    );
  };

  const getAvailableCategories = useCallback(
    (currentIndex) => {
      return expensesCategories.filter(
        (expense) =>
          !expenseSplits.some(
            (split, splitIndex) =>
              splitIndex !== currentIndex &&
              split.categoryId === expense.id.toString()
          )
      );
    },
    [expensesCategories, expenseSplits]
  );

  const updateSplit = (index, field, value) => {
    const newSplits = [...expenseSplits];
    if (field === "price" || field === "amount") {
      newSplits[index][field] = value === "" ? "" : parseInt(value);
    } else {
      newSplits[index][field] = value;
    }
    setExpenseSplits(newSplits);
  };

  const isButtonDisabled = () => {
    if (loading) return true;
    if (errorMsg) {
      return true;
    } else if (!paymentMethodId || paymentMethodId === "") {
      return true;
    } else if (!totalPrice || totalPrice === "") {
      return true;
    } else {
      const validSplits = expenseSplits.filter(
        (split) => split.categoryId && split.price && split.amount
      );
      if (validSplits.length === 0) {
        return true;
      }

      const splitsTotal = validSplits.reduce(
        (sum, split) => sum + (parseInt(split.price) || 0),
        0
      );
      const totalPriceNum = parseInt(totalPrice) || 0;
      return splitsTotal !== totalPriceNum;
    }
  };

  const getSplitsSummary = () => {
    const formatCurrency = (value) =>
      "$" + new Intl.NumberFormat("es-CL").format(value);

    const splitsSum = expenseSplits
      .filter((s) => s.price)
      .reduce((sum, s) => sum + (parseInt(s.price) || 0), 0);
    const totalNum = parseInt(totalPrice) || 0;
    const diff = totalNum - splitsSum;

    return { splitsSum, totalNum, diff, formatCurrency };
  };

  const handleDateKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      totalPriceRef.current && totalPriceRef.current.focus();
    }
  };

  const handleTotalPriceKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      splitRefs.current.category[0] && splitRefs.current.category[0].focus();
    }
  };

  const openSelectOnMouseDown = () => {
    mouseInteractedRef.current = true;
    setTimeout(() => {
      mouseInteractedRef.current = false;
    }, 0);
  };

  const openSelectOnFocus = (event) => {
    if (mouseInteractedRef.current) return;
    if (event.target && event.target.showPicker) {
      try {
        event.target.showPicker();
      } catch {
        // ignore browsers that require a direct user gesture
      }
    }
  };

  const handleSplitPriceKeyDown = (index) => (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      splitRefs.current.amount[index] &&
        splitRefs.current.amount[index].focus();
    }
  };

  const handleSplitAmountKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addSplitRef.current && addSplitRef.current.focus();
    }
  };

  const handleAddSplitKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const newIndex = expenseSplits.length;
      addSplit();
      setNextFocus({ field: "category", index: newIndex });
    } else if (event.key === "Escape") {
      event.preventDefault();
      paymentMethodRef.current && paymentMethodRef.current.focus();
    }
  };

  const handleDescriptionKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (!isButtonDisabled()) {
        submitRef.current && submitRef.current.focus();
      }
    }
  };

  if (isLoadingInvoice) {
    return (
      <div className="text-center p-4">
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        <p className="mt-2">Cargando datos de la factura...</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer
        position="top-center"
        className="toas-container p-3 position-fixed top-0 start-0 end-0"
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            onClose={() =>
              setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }
            bg={toast.variant}
            autohide
            delay={3000}
            className="mb-2"
          >
            <Toast.Header closeButton>
              <strong className="me-auto">¡Atención!</strong>
            </Toast.Header>
            <Toast.Body className="text-white">{toast.message}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>

      <div>
        <div className="row">
          <div className="col-md-12 mt-2">
            <label htmlFor="new-expense-date-picker" className="col-form-label">
              Fecha
            </label>
            <div>
              <DatePicker
                id={
                  isUpdateMode
                    ? "update-expense-date-picker"
                    : "new-expense-date-picker"
                }
                selected={date}
                dateFormat="yyyy/MM/dd"
                onChange={datePickerOnChange}
                preventOpenOnFocus
                onKeyDown={handleDateKeyDown}
              />
            </div>
          </div>
          <div className="col-md-12 mt-2">
            <label className="col-form-label" htmlFor="new-expense-price">
              Valor Total
            </label>
            <input
              id={isUpdateMode ? "update-expense-price" : "new-expense-price"}
              type="number"
              className="form-control"
              placeholder="Valor Total"
              value={totalPrice === 0 ? "" : totalPrice}
              onChange={totalPriceChange}
              ref={totalPriceRef}
              onKeyDown={handleTotalPriceKeyDown}
            />
          </div>
          <div className="col-md-12 mt-2">
            <div className="col-form-label">División de Gastos</div>
            <div className="expense-splits-container">
              {expenseSplits.map((split, index) => (
                <div key={index} className="row mb-2 split-row">
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={split.categoryId}
                      onChange={(e) => {
                        updateSplit(index, "categoryId", e.target.value);
                        splitRefs.current.price[index] &&
                          splitRefs.current.price[index].focus();
                      }}
                      ref={(element) =>
                        (splitRefs.current.category[index] = element)
                      }
                      onFocus={openSelectOnFocus}
                      onMouseDown={openSelectOnMouseDown}
                    >
                      <option value="" disabled>
                        Categoria
                      </option>
                      {getAvailableCategories(index).map((expense, i) => (
                        <option value={expense.id} key={i}>
                          {expense.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Precio"
                      value={split.price === 0 ? "" : split.price}
                      onChange={(e) =>
                        updateSplit(index, "price", e.target.value)
                      }
                      ref={(element) =>
                        (splitRefs.current.price[index] = element)
                      }
                      onKeyDown={handleSplitPriceKeyDown(index)}
                    />
                  </div>
                  <div className="col-md-3">
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Can"
                        value={split.amount === 0 ? "" : split.amount}
                        onChange={(e) =>
                          updateSplit(index, "amount", e.target.value)
                        }
                        min="1"
                        ref={(element) =>
                          (splitRefs.current.amount[index] = element)
                        }
                        onKeyDown={handleSplitAmountKeyDown}
                      />
                      <span className="input-group-text">
                        {expensesCategories.find(
                          (cat) => cat.id === parseInt(split.categoryId)
                        )?.unitType || ""}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-2">
                    {expenseSplits.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeSplit(index)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary btn-sm mt-2"
                onClick={addSplit}
                ref={addSplitRef}
                onKeyDown={handleAddSplitKeyDown}
              >
                <i className="fa-solid fa-plus"></i> Agregar división
              </button>
              {(() => {
                const { splitsSum, totalNum, diff, formatCurrency } =
                  getSplitsSummary();

                return (
                  <div className="mt-2 p-2 bg-light rounded">
                    <div className="d-flex justify-content-between">
                      <span>Suma categorías:</span>
                      <span>{formatCurrency(splitsSum)}</span>
                    </div>
                    <div className="d-flex justify-content-between fw-bold">
                      <span>Total factura:</span>
                      <span>{formatCurrency(totalNum)}</span>
                    </div>
                    <div
                      className={`d-flex justify-content-between ${diff > 0 ? "text-danger" : diff < 0 ? "text-warning" : "text-success"}`}
                    >
                      <span>
                        {diff > 0
                          ? "Faltante"
                          : diff < 0
                            ? "Sobrante"
                            : "Cuadrado"}
                      </span>
                      <span>
                        {diff === 0 ? "✓" : formatCurrency(Math.abs(diff))}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="col-md-12 mt-2">
            <label
              className="col-form-label"
              htmlFor="new-expense-payment-method"
            >
              Tipo de pago
            </label>
            <select
              id={
                isUpdateMode
                  ? "update-expense-payment-method"
                  : "new-expense-payment-method"
              }
              className="form-select"
              value={paymentMethodId}
              onChange={(e) => {
                setPaymentMethodId(e.target.value);
                descriptionRef.current && descriptionRef.current.focus();
              }}
              ref={paymentMethodRef}
              onFocus={openSelectOnFocus}
              onMouseDown={openSelectOnMouseDown}
            >
              <option value="" disabled>
                Tipo de pago
              </option>
              {PAYMENT_METHOD.map((method, i) => (
                <option value={method.id} key={i}>
                  {" "}
                  {method.name}{" "}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-12 mt-2">
            <label className="col-form-label" htmlFor="new-expense-description">
              Descripcion (Opcional)
            </label>
            <textarea
              id={
                isUpdateMode
                  ? "update-expense-description"
                  : "new-expense-description"
              }
              type="text"
              className="form-control"
              value={description}
              onChange={descriptionChange}
              ref={descriptionRef}
              onKeyDown={handleDescriptionKeyDown}
            />
          </div>
          <div className="col-md-12 mt-2">
            <label className="col-form-label" htmlFor="new-expense-invoice">
              Factura (Opcional)
            </label>
            <div className="d-flex">
              <label
                className="take-photo take-photo d-flex flex-column align-items-center justify-center"
                htmlFor={
                  isUpdateMode
                    ? "update-expense-invoice"
                    : "new-expense-invoice"
                }
              >
                <i className="fa-solid fa-camera"></i>
                <span>Tomar foto</span>
              </label>
              <input
                className="d-none"
                type="file"
                accept="image/*"
                id={
                  isUpdateMode
                    ? "update-expense-invoice"
                    : "new-expense-invoice"
                }
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              {imageSrc && (
                <img
                  alt="expense preview"
                  className="expense-preview ms-5"
                  src={
                    imageSrc.startsWith("data:")
                      ? imageSrc
                      : `${imageSrc}?t=${cacheBuster}`
                  }
                />
              )}
            </div>
          </div>
        </div>
        {errorMsg && (
          <div className="row mt-4">
            <p className="error-msg">{errorMsg}</p>
          </div>
        )}
        <div className="row justify-content-center mt-5">
          <div className="col-6 col-md-5">
            <button
              type="button"
              className="btn btn-primary btn-sm w-100 text-modal"
              onClick={addNewExpense}
              disabled={isButtonDisabled()}
              ref={submitRef}
            >
              {" "}
              {buttonContent()}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewExpense;
